import React from "react";
import { observer } from "mobx-react";
import { DesignShowcaseUpdateStore } from "../../store";
import { DesignShowcaseUpdateForm } from "./DesignShowcaseUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { ShowcaseStatus } from "../../../../api/graphql";

export type DesignShowcaseUpdateProps = {
  designShowcaseUpdateStore: DesignShowcaseUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const DesignShowcaseUpdate: React.FC<DesignShowcaseUpdateProps> =
  observer(
    ({ designShowcaseUpdateStore, showToolbox = true, showSaveFooter }) => {
      return (
        <>
          <SavingOverlay open={designShowcaseUpdateStore.isSaving} />
          <OutdatedAfterSaveNotice
            open={designShowcaseUpdateStore.savedButOutdated}
            onReload={() => designShowcaseUpdateStore.reloadDesignShowcase()}
            reloadDisabled={designShowcaseUpdateStore.isProcessing}
          />
          <DesignShowcaseUpdateForm
            designShowcaseUpdateStore={designShowcaseUpdateStore}
            codeError={designShowcaseUpdateStore.errors.code?.message}
            onCodeChange={(value: string) => {
              designShowcaseUpdateStore.setCodeValue(value);
            }}
            code={designShowcaseUpdateStore.entityValues.code}
            nameError={designShowcaseUpdateStore.errors.name?.message}
            onNameChange={(value: string) => {
              designShowcaseUpdateStore.setNameValue(value);
            }}
            name={designShowcaseUpdateStore.entityValues.name}
            summaryError={designShowcaseUpdateStore.errors.summary?.message}
            onSummaryChange={(value: string) => {
              designShowcaseUpdateStore.setSummaryValue(value);
            }}
            summary={designShowcaseUpdateStore.entityValues.summary}
            statusError={designShowcaseUpdateStore.errors.status?.message}
            statusOptions={designShowcaseUpdateStore.getStatusOptions()}
            onStatusChange={(value: ShowcaseStatus) => {
              designShowcaseUpdateStore.setStatusValue(value);
            }}
            status={designShowcaseUpdateStore.entityValues.status}
            categoryError={designShowcaseUpdateStore.errors.category?.message}
            onCategoryChange={(value) => {
              designShowcaseUpdateStore.setCategoryValue(value);
            }}
            categorySelectedValue={
              designShowcaseUpdateStore.entityValues.showCategory
            }
            onCategorySelected={(value) => {
              designShowcaseUpdateStore.setSelectedCategory(value);
            }}
            categoryOptions={designShowcaseUpdateStore.getCategoryOptions()}
            relatedCategoriesError={
              designShowcaseUpdateStore.errors.relatedCategories?.message
            }
            onRelatedCategoriesChange={(value) => {
              designShowcaseUpdateStore.setRelatedCategoriesValue(value);
            }}
            relatedCategoriesSelectedValues={
              designShowcaseUpdateStore.entityValues.showRelatedCategories
            }
            onRelatedCategoriesSelected={(value) => {
              designShowcaseUpdateStore.setSelectedRelatedCategories(value);
            }}
            relatedCategoriesOptions={designShowcaseUpdateStore.getRelatedCategoriesOptions()}
            appliedStatusesError={
              designShowcaseUpdateStore.errors.appliedStatuses?.message
            }
            appliedStatusesOptions={designShowcaseUpdateStore.getAppliedStatusesOptions()}
            onAppliedStatusesChange={(value: ShowcaseStatus[]) => {
              designShowcaseUpdateStore.setAppliedStatusesValue(value);
            }}
            appliedStatuses={
              designShowcaseUpdateStore.entityValues.appliedStatuses
            }
            lifecycleValue={
              designShowcaseUpdateStore.entityValues.showLifecycle?.id || ""
            }
            lifecycleTransitions={
              designShowcaseUpdateStore.lifecycleTransitions
            }
            onLifecycleTransition={(transitionName: string) =>
              designShowcaseUpdateStore.onLifecycleTransition(transitionName)
            }
            lifecycleIsProcessing={
              designShowcaseUpdateStore.lifecycleTransitionProcessing
            }
            quantityError={designShowcaseUpdateStore.errors.quantity?.message}
            onQuantityChange={(value: number) => {
              designShowcaseUpdateStore.setQuantityValue(value);
            }}
            quantity={designShowcaseUpdateStore.entityValues.quantity}
            durationInYearsError={
              designShowcaseUpdateStore.errors.durationInYears?.message
            }
            onDurationInYearsChange={(value: number | undefined) => {
              designShowcaseUpdateStore.setDurationInYearsValue(value);
            }}
            durationInYears={
              designShowcaseUpdateStore.entityValues.durationInYears
            }
            amountError={designShowcaseUpdateStore.errors.amount?.message}
            onAmountChange={(value: number) => {
              designShowcaseUpdateStore.setAmountValue(value);
            }}
            amount={designShowcaseUpdateStore.entityValues.amount}
            ratioError={designShowcaseUpdateStore.errors.ratio?.message}
            onRatioChange={(value: number | undefined) => {
              designShowcaseUpdateStore.setRatioValue(value);
            }}
            ratio={designShowcaseUpdateStore.entityValues.ratio}
            startDateError={designShowcaseUpdateStore.errors.startDate?.message}
            onStartDateChange={(value) => {
              designShowcaseUpdateStore.setStartDateValue(value);
            }}
            startDate={designShowcaseUpdateStore.entityValues.startDate}
            expiresAtError={designShowcaseUpdateStore.errors.expiresAt?.message}
            onExpiresAtChange={(value) => {
              designShowcaseUpdateStore.setExpiresAtValue(value);
            }}
            expiresAt={designShowcaseUpdateStore.entityValues.expiresAt}
            registeredAtError={
              designShowcaseUpdateStore.errors.registeredAt?.message
            }
            onRegisteredAtChange={(value) => {
              designShowcaseUpdateStore.setRegisteredAtValue(value);
            }}
            registeredAt={designShowcaseUpdateStore.entityValues.registeredAt}
            activeError={designShowcaseUpdateStore.errors.active?.message}
            onActiveChange={(value: boolean) => {
              designShowcaseUpdateStore.setActiveValue(value);
            }}
            active={designShowcaseUpdateStore.entityValues.active}
            highlightedError={
              designShowcaseUpdateStore.errors.highlighted?.message
            }
            onHighlightedChange={(value: boolean) => {
              designShowcaseUpdateStore.setHighlightedValue(value);
            }}
            highlighted={designShowcaseUpdateStore.entityValues.highlighted}
            phoneError={designShowcaseUpdateStore.errors.phone?.message}
            onPhoneChange={(value: string) => {
              designShowcaseUpdateStore.setPhoneValue(value);
            }}
            phone={designShowcaseUpdateStore.entityValues.phone}
            taxIdError={designShowcaseUpdateStore.errors.taxId?.message}
            onTaxIdChange={(value: string) => {
              designShowcaseUpdateStore.setTaxIdValue(value);
            }}
            taxId={designShowcaseUpdateStore.entityValues.taxId}
            attachmentOnRestoreFile={(id: string) =>
              designShowcaseUpdateStore.attachmentRestoreFile(id)
            }
            attachmentOnLoadFile={(file: any) =>
              designShowcaseUpdateStore.attachmentUploadFile(file)
            }
            attachmentOnCommitFile={(changes) =>
              designShowcaseUpdateStore.attachmentCommitFile(changes)
            }
            attachmentFiles={
              designShowcaseUpdateStore.entityValues.__attachment_files
            }
            attachmentError={
              designShowcaseUpdateStore.errors.attachment?.message
            }
            anyFileOnRestoreFile={(id: string) =>
              designShowcaseUpdateStore.anyFileRestoreFile(id)
            }
            anyFileOnLoadFile={(file: any) =>
              designShowcaseUpdateStore.anyFileUploadFile(file)
            }
            anyFileOnCommitFile={(changes) =>
              designShowcaseUpdateStore.anyFileCommitFile(changes)
            }
            anyFileFiles={
              designShowcaseUpdateStore.entityValues.__anyFile_files
            }
            anyFileError={designShowcaseUpdateStore.errors.anyFile?.message}
            avatarData={
              designShowcaseUpdateStore.designShowcaseInstance?.avatar_data
            }
            avatarEntityId={
              designShowcaseUpdateStore.designShowcaseInstance?.id || ""
            }
            avatarPreviewUrl={designShowcaseUpdateStore.avatarPreviewUrl}
            onAvatarChange={(blob: Blob) =>
              designShowcaseUpdateStore.avatarUploadBlob(blob)
            }
            onAvatarDelete={() => designShowcaseUpdateStore.avatarDelete()}
            keywordsError={designShowcaseUpdateStore.errors.keywords?.message}
            onKeywordsChange={(value: string[]) => {
              designShowcaseUpdateStore.setKeywordsValue(value);
            }}
            keywords={designShowcaseUpdateStore.entityValues.keywords}
            bodyError={designShowcaseUpdateStore.errors.body?.message}
            onBodyChange={(value: string) => {
              designShowcaseUpdateStore.setBodyValue(value);
            }}
            body={designShowcaseUpdateStore.entityValues.body}
            metadataError={designShowcaseUpdateStore.errors.metadata?.message}
            onMetadataChange={(value: string) => {
              designShowcaseUpdateStore.setMetadataValue(value);
            }}
            metadata={designShowcaseUpdateStore.entityValues.metadata}
            internalNoteError={
              designShowcaseUpdateStore.errors.internalNote?.message
            }
            onInternalNoteChange={(value: string) => {
              designShowcaseUpdateStore.setInternalNoteValue(value);
            }}
            internalNote={designShowcaseUpdateStore.entityValues.internalNote}
            apiKeyError={designShowcaseUpdateStore.errors.apiKey?.message}
            onApiKeyChange={(value: string) => {
              designShowcaseUpdateStore.setApiKeyValue(value);
            }}
            apiKey={designShowcaseUpdateStore.entityValues.apiKey}
            showcaseLineUpdateStore={designShowcaseUpdateStore.linesUpdateStore}
            linesVisibleColumns={designShowcaseUpdateStore.linesVisibleColumns}
            linesColumnTypes={designShowcaseUpdateStore.linesColumnTypes}
            linesOnRestore={(id: string) =>
              designShowcaseUpdateStore.linesRestore(id)
            }
            linesOnCommitChanges={(changes) =>
              designShowcaseUpdateStore.linesCommitChanges(changes)
            }
            linesOnCommitInlineChange={(rowId, changes) =>
              designShowcaseUpdateStore.linesCommitInlineChange(rowId, changes)
            }
            linesValidateChanges={(changes) =>
              designShowcaseUpdateStore.linesValidateChanges(changes)
            }
            linesRows={designShowcaseUpdateStore.entityValues.showLines}
            linesError={designShowcaseUpdateStore.errors.lines?.message}
            dummyCRUDRestoredObservable={
              designShowcaseUpdateStore.dummyCRUDRestoredObservable
            }
            showToolbox={showToolbox}
            showSaveFooter={showSaveFooter}
            saving={designShowcaseUpdateStore.saveDisabled}
            onSave={() => designShowcaseUpdateStore.saveDesignShowcase()}
            onCancel={() => designShowcaseUpdateStore.cancelDesignShowcase()}
            viewFormCategory={() =>
              designShowcaseUpdateStore.viewFormCategory()
            }
          />
          <ErrorDialog
            open={designShowcaseUpdateStore.errorDialogOpened}
            title={designShowcaseUpdateStore.errorTitle}
            description={designShowcaseUpdateStore.errorDescription}
            onClose={() => designShowcaseUpdateStore.closeErrorDialog()}
          />
          <ConfirmCancelDialog
            open={designShowcaseUpdateStore.confirmDialogOpened}
            title={designShowcaseUpdateStore.confirmDialogTitle}
            description={designShowcaseUpdateStore.confirmDialogDescription}
            onConfirm={() => designShowcaseUpdateStore.executeConfirmedAction()}
            onCancel={() => designShowcaseUpdateStore.closeConfirmDialog()}
          />
        </>
      );
    }
  );
